// SidebarBucketContent.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Icons
import Icon from 'svg/Icon';

// Components
import SidebarButton from 'main/sidebar/button/SidebarButton';
import SidebarBucketAddButton from './addbutton/SidebarBucketAddButton';

/**
 * SidebarBucketContent
 *
 * Renders the database, dashboard, and channel sections for a sidebar bucket.
 *
 * @param {object} bucket - Bucket data with dashboards, databases, and channels.
 * @param {boolean} isOpen - Whether the bucket content should be expanded.
 * @param {boolean} showHeader - Whether the parent bucket renders a header.
 * @param {function} onDashboardClick - Called when a dashboard row is clicked.
 * @param {function} onChannelClick - Called when a channel row is clicked.
 * @param {function} onDatabaseClick - Called when a database row is clicked.
 * @param {function} onAddFromTemplate - Called when the add dashboard action is clicked.
 * @param {function} onAddChannel - Called when the add channel action is clicked.
 * @param {function} onAddDatabase - Called when the add database action is clicked.
 *
 * @returns {ReactNode} The rendered sidebar bucket content.
 */
const SidebarBucketContent = ({
    bucket,
    isOpen,
    showHeader,
    onDashboardClick,
    onChannelClick,
    onDatabaseClick,
    onAddFromTemplate,
    onAddChannel,
    onAddDatabase,
}) => {
    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- RENDER ----- //

    return (
        <div
            className={`sidebar-bucket-content ${isOpen ? 'is-open' : ''}`}
            style={{
                padding: showHeader ? '10px 10px 10px 20px' : 0,
            }}
        >
            {/* DATABASES */}
            <div className="sidebar-bucket-subtitle">
                Databases
            </div>

            {bucket?.databases?.length > 0 && (
                <div className="sidebar-bucket-list">
                    {bucket.databases.map((row) => (
                        <SidebarButton
                            key={row.key}
                            iconKey={row.iconKey}
                            label={row.label}
                            onClick={() => onDatabaseClick?.(row.database)}
                        />
                    ))}
                </div>
            )}

            <SidebarBucketAddButton
                label="Create Database"
                onClick={() => onAddDatabase?.()}
            />

            {/* DASHBOARDS */}
            <div className="sidebar-bucket-subtitle">
                Dashboards
            </div>

            {bucket?.dashboards?.length > 0 && (
                <div className="sidebar-bucket-list">
                    {bucket.dashboards.map((dashboardRow) => {
                        const showPinned = dashboardRow?.pinned === true;
                        const showPrivate = dashboardRow?.private === true;

                        return (
                            <SidebarButton
                                key={dashboardRow.key}
                                iconKey={dashboardRow?.iconKey || 'dashboard'}
                                label={dashboardRow?.title || 'Untitled Dashboard'}
                                rightContent={showPinned || showPrivate ? (
                                    <div
                                        style={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            gap: 6,
                                        }}
                                    >
                                        {showPinned && (
                                            <Icon
                                                iconKey="keep"
                                                color={theme.foregroundMuted}
                                                width={16}
                                                height={16}
                                            />
                                        )}
                                        {showPrivate && (
                                            <Icon
                                                iconKey="lock"
                                                color={theme.foregroundMuted}
                                                width={16}
                                                height={16}
                                            />
                                        )}
                                    </div>
                                ) : null}
                                onClick={() => onDashboardClick?.(dashboardRow)}
                            />
                        );
                    })}
                </div>
            )}

            {bucket?.key === 'workspace' && (
                <SidebarBucketAddButton
                    label="Create Dashboard"
                    onClick={() => onAddFromTemplate?.()}
                />
            )}

            {/* CHANNELS */}
            <div className="sidebar-bucket-subtitle">
                Channels
            </div>

            {bucket?.channels?.length > 0 && (
                <div className="sidebar-bucket-list">
                    {bucket.channels.map((channelRow) => (
                        <SidebarButton
                            key={channelRow.key}
                            iconKey={channelRow?.iconKey || 'tag'}
                            label={channelRow?.title || 'Untitled'}
                            onClick={() => onChannelClick?.(channelRow)}
                        />
                    ))}
                </div>
            )}

            <SidebarBucketAddButton
                label="Create Channel"
                onClick={() => onAddChannel?.(bucket)}
            />
        </div>
    );
};

export default SidebarBucketContent;